import { CogNeedTextField } from '@cogneed/react-utils';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Fade,
  FormControl, FormControlLabel, MenuItem,
  RadioGroup,
  Typography,
} from '@mui/material';
import { addMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { isNaN } from 'lodash';
import React from 'react';
import useFreeResiliationFormulaContext from '../../Contexts/FreeResiliationFormulaContext/useFreeResiliationFormulaContext';
import CogNeedKeyboardDatePicker from '../../../../../../CogNeedKeyboardDatePicker';
import CogNeedRadio from '../../../../../../CogNeedRadio';

const SECTION_KEY = 'commitment';

export default function CommitmentSection() {
  const {
    state, updateState, setExpanded, expanded, completedSteps: { commitment: isCompleted },
  } = useFreeResiliationFormulaContext();

  function setCommitmentDuration(value: 12 | 24 | 0) {
    updateState((prev) => ({ ...prev, commitmentDuration: value }));
  }

  function setStartDate(value: Date) {
    updateState((prev) => ({ ...prev, startDate: value }));
  }

  function setHasEquipment(value: boolean) {
    updateState((prev) => ({ ...prev, hasEquipment: value }));
  }

  const { t } = useTranslation('wysiwyg');

  const defaultValue = React.useMemo(() => {
    if (state.hasEquipment === undefined) return '';
    return state.hasEquipment ? 'equipment' : 'no-equipment';
  }, [state.hasEquipment]);

  return (
    <Accordion
      expanded={expanded === SECTION_KEY}
      onChange={() => { setExpanded(expanded === SECTION_KEY ? null : SECTION_KEY); }}
      className="section"
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary expandIcon={(!isCompleted || expanded === SECTION_KEY) ? <ExpandMoreIcon /> : <CheckCircleOutlineIcon className="checked" />}>
        {t('resiliation-fee.commitment-section.title')}
      </AccordionSummary>
      <AccordionDetails>
        <FormControl>
          <Typography variant="h4">
            {t('resiliation-fee.commitment-section.initial-commitment.title')}
          </Typography>
          <CogNeedTextField
            select
            sx={{ width: 230 }}
            defaultValue={state.commitmentDuration ?? ''}
            onChange={(e) => {
              const { value } = e.target;
              const newValue = parseInt(value, 10) as 0 | 12 | 24;
              setCommitmentDuration(newValue);
            }}
            SelectProps={{
              displayEmpty: true,
              MenuProps: {
                anchorOrigin: { vertical: 'bottom', horizontal: 2 },
                transformOrigin: { vertical: -6, horizontal: 2 },
              },
              renderValue(value): React.ReactNode {
                if (value !== '') {
                  return t(`resiliation-fee.commitment-section.initial-commitment.${value}`);
                }
                return t('resiliation-fee.commitment-section.initial-commitment.placeholder');
              },
            }}
          >
            {['0', '12', '24'].map((value) => (
              <MenuItem key={value} value={value}>
                {t(`resiliation-fee.commitment-section.initial-commitment.${value}`)}
              </MenuItem>
            ))}
          </CogNeedTextField>
        </FormControl>
        <Fade in={!!state.commitmentDuration} mountOnEnter unmountOnExit>
          <FormControl>
            <Typography variant="h4">
              {t('resiliation-fee.commitment-section.end-of-commitment.title')}
            </Typography>
            {!!state.commitmentDuration && (
              <CogNeedKeyboardDatePicker
                disablePast
                inputFormat="dd/MM/yyyy"
                InputProps={{
                  sx: { width: 175 },
                }}
                value={state.startDate ? addMonths(state.startDate, state.commitmentDuration) : null}
                maxDate={addMonths(Date.now(), state.commitmentDuration)}
                onChange={(date: Date | null) => {
                  if (date && !isNaN(date.getTime()) && state.commitmentDuration) {
                    setStartDate(addMonths(date, -state.commitmentDuration));
                  }
                }}
              />
            )}
          </FormControl>
        </Fade>
        <Fade in={state.commitmentDuration === 24 && state.startDate && state.startDate.getFullYear() > 2022} mountOnEnter unmountOnExit>
          <FormControl>
            <Typography variant="h4">
              {t('resiliation-fee.commitment-section.has-equipment.title')}
            </Typography>
            <Typography variant="body1">
              {t('resiliation-fee.commitment-section.has-equipment.description')}
            </Typography>
            <RadioGroup
              onChange={(e) => { setHasEquipment(e.target.value === 'equipment'); }}
              className="radio"
              defaultValue={defaultValue}
            >
              <FormControlLabel value="equipment" control={<CogNeedRadio />} label={t('resiliation-fee.commitment-section.has-equipment.true')} />
              <FormControlLabel value="no-equipment" control={<CogNeedRadio />} label={t('resiliation-fee.commitment-section.has-equipment.false')} />
            </RadioGroup>
          </FormControl>
        </Fade>
      </AccordionDetails>
    </Accordion>
  );
}
